import { computed, ref } from 'vue'

export type ColorTheme = 'light' | 'dark'

const STORAGE_KEY = 'poqo-color-theme'
const colorTheme = ref<ColorTheme>('light')
let initialized = false
let hasStoredPreference = false

function readStoredTheme(): ColorTheme | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'dark' || stored === 'light' ? stored : null
  } catch {
    return null
  }
}

function getSystemTheme(): ColorTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function persistTheme(theme: ColorTheme) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Theme persistence is cosmetic; ignore storage failures.
  }
}

function applyTheme(theme: ColorTheme) {
  colorTheme.value = theme

  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.dataset.theme = theme
  root.style.colorScheme = theme
}

export function initializeColorTheme() {
  if (initialized) return
  initialized = true

  const storedTheme = readStoredTheme()
  hasStoredPreference = storedTheme !== null
  applyTheme(storedTheme ?? getSystemTheme())

  if (typeof window === 'undefined' || !window.matchMedia) return

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', (event) => {
    if (!hasStoredPreference) {
      applyTheme(event.matches ? 'dark' : 'light')
    }
  })
}

export function useColorTheme() {
  initializeColorTheme()

  const isDark = computed(() => colorTheme.value === 'dark')

  function setColorTheme(theme: ColorTheme) {
    hasStoredPreference = true
    persistTheme(theme)
    applyTheme(theme)
  }

  function toggleColorTheme() {
    setColorTheme(colorTheme.value === 'dark' ? 'light' : 'dark')
  }

  return {
    colorTheme,
    isDark,
    setColorTheme,
    toggleColorTheme,
  }
}
